import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  PageHeader,
  Button,
  Card,
  CardHeader,
  Badge,
  Spinner,
  Alert,
  Modal,
  ModalFooter,
} from '@orientamos/ui'
import {
  Edit,
  Download,
  Printer,
  CheckCircle,
  Send,
  FileText,
  Building2,
  User,
  Calendar,
  Banknote,
  ClipboardList,
  Maximize2,
  X,
} from 'lucide-react'
import {
  getContract,
  approveContract,
  exportContractHtml,
  exportContractPdfBlob,
  type ContractDetail,
  type ContractStatus,
} from '@lib/api'
import { printContract } from '@lib/printContract'
import { buildContractSrcDoc } from '@lib/contractPreview'
import { buildWordHtml } from '@lib/buildWordHtml'
import { SendContractModal } from '@features/contracts/components/SendContractModal'
import { formatDate, formatCurrency } from '@orientamos/shared'

const statusBadgeVariants: Record<
  ContractStatus,
  'default' | 'warning' | 'success' | 'primary' | 'secondary'
> = {
  draft: 'default',
  pending_review: 'warning',
  approved: 'success',
  sent: 'primary',
  signed: 'secondary',
}

const statusLabels: Record<ContractStatus, string> = {
  draft: 'Borrador',
  pending_review: 'En revision',
  approved: 'Aprobado',
  sent: 'Enviado',
  signed: 'Firmado',
}

export default function ContractDetailPage() {
  const { id } = useParams<{ id: string }>()

  const [contract, setContract] = useState<ContractDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isApproving, setIsApproving] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isExportingDocx, setIsExportingDocx] = useState(false)

  const [approveModalOpen, setApproveModalOpen] = useState(false)
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false)
  const [fullscreenOpen, setFullscreenOpen] = useState(false)

  useEffect(() => {
    if (!id) return

    const loadContract = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const result = await getContract(id)
        if (result.success && result.data) {
          setContract(result.data)
        } else {
          setError(result.error || 'Contrato no encontrado')
        }
      } catch (err) {
        console.error('Error loading contract:', err)
        setError('Error al cargar el contrato')
      } finally {
        setIsLoading(false)
      }
    }

    loadContract()
  }, [id])

  const handleApprove = async () => {
    if (!id) return

    setIsApproving(true)
    try {
      const result = await approveContract(id)
      if (result.success) {
        setContract((prev) => (prev ? { ...prev, status: 'approved' as ContractStatus } : null))
        setApproveModalOpen(false)
      } else {
        setError(result.error || 'Error al aprobar el contrato')
      }
    } catch (err) {
      console.error('Error approving:', err)
      setError('Error al aprobar el contrato')
    } finally {
      setIsApproving(false)
    }
  }

  const handleEmailSent = () => {
    // Refresh contract state after email is sent
    setContract((prev) => (prev ? { ...prev, status: 'sent' as ContractStatus } : null))
  }

  const handleExportDocx = useCallback(async () => {
    if (!id || !contract) return

    setIsExportingDocx(true)
    try {
      const result = await exportContractHtml(id)
      if (result.success && result.data) {
        const wordHtml = buildWordHtml(result.data.html)
        const blob = new Blob([wordHtml], {
          type: 'application/msword',
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `contrato-${contract.orderNumber || 'nuevo'}.doc`
        a.click()
        URL.revokeObjectURL(url)
      } else {
        setError(result.error || 'Error al exportar el contrato')
      }
    } catch (err) {
      console.error('Error exporting DOCX:', err)
      setError('Error al exportar el contrato como DOCX')
    } finally {
      setIsExportingDocx(false)
    }
  }, [id, contract])

  const handlePrint = useCallback(async () => {
    if (!id || !contract) return

    setIsPrinting(true)
    try {
      const result = await exportContractHtml(id)
      if (result.success && result.data) {
        printContract(result.data.html)
      } else {
        setError(result.error || 'Error al imprimir el contrato')
      }
    } catch (err) {
      console.error('Error printing:', err)
      setError('Error al imprimir el contrato')
    } finally {
      setIsPrinting(false)
    }
  }, [id, contract])

  const handleExportPdf = useCallback(async () => {
    if (!id || !contract) return

    setIsExportingPdf(true)
    try {
      const result = await exportContractPdfBlob(id)
      if (result.success && result.data) {
        const url = URL.createObjectURL(result.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `contrato-${contract.orderNumber || 'nuevo'}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } else {
        setError(result.error || 'Error al exportar el contrato')
      }
    } catch (err) {
      console.error('Error exporting PDF:', err)
      setError('Error al exportar el contrato')
    } finally {
      setIsExportingPdf(false)
    }
  }, [id, contract])

  const previewSrcDoc = useMemo(
    () => (contract?.renderedHtml ? buildContractSrcDoc(contract.renderedHtml) : null),
    [contract?.renderedHtml]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !contract) {
    return (
      <div>
        <PageHeader
          title="Contrato"
          breadcrumbs={[
            { label: 'Inicio', href: '/' },
            { label: 'Contratos', href: '/contracts' },
            { label: 'Detalle' },
          ]}
        />
        <Alert variant="danger">{error || 'Contrato no encontrado'}</Alert>
      </div>
    )
  }

  // Extract data from snapshot
  const snapshot = contract.dataSnapshot as {
    intake?: {
      orderNumber?: string
      contractType?: string
      guaranteedRent?: boolean
      property?: Record<string, unknown>
      terms?: Record<string, unknown>
    }
    parties?: Array<{
      role: string
      firstName: string
      lastName: string
      idNumber: string
      phone: string
      email: string
      address: string
      city: string
      personType: string
      companyName?: string
    }>
  }

  const intakeData = snapshot?.intake || {}
  const partiesData = snapshot?.parties || []

  const tenant = partiesData.find((p) => p.role === 'tenant')
  const owners = partiesData.filter((p) => p.role === 'owner')
  const guarantors = partiesData.filter((p) => p.role === 'guarantor')

  const property = intakeData.property || {}
  const terms = intakeData.terms || {}

  const canEdit = contract.status === 'draft' || contract.status === 'pending_review'
  const canApprove = contract.status === 'draft' || contract.status === 'pending_review'
  const canSend = contract.status === 'approved'

  return (
    <div>
      <PageHeader
        title={`Contrato - ${contract.orderNumber}`}
        description={`${contract.templateName} - Version ${contract.version}`}
        breadcrumbs={[
          { label: 'Inicio', href: '/' },
          { label: 'Contratos', href: '/contracts' },
          { label: contract.orderNumber },
        ]}
        actions={
          <div className="flex gap-2">
            {canEdit && (
              <Link to={`/contracts/${contract.id}/edit`}>
                <Button variant="outline">
                  <Edit className="w-4 h-4 mr-1" />
                  Editar
                </Button>
              </Link>
            )}
            {canApprove && (
              <Button
                variant="outline"
                onClick={() => setApproveModalOpen(true)}
                className="text-success-600 border-success-600 hover:bg-success-50"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Aprobar
              </Button>
            )}
            {canSend && (
              <Button
                variant="outline"
                onClick={() => setSendEmailModalOpen(true)}
                className="text-primary-600 border-primary-600 hover:bg-primary-50"
              >
                <Send className="w-4 h-4 mr-1" />
                Enviar por Correo
              </Button>
            )}
            <Button onClick={handleExportPdf} loading={isExportingPdf}>
              <Download className="w-4 h-4 mr-1" />
              Exportar PDF
            </Button>
            <Button variant="outline" onClick={handlePrint} loading={isPrinting}>
              <Printer className="w-4 h-4 mr-1" />
              Imprimir
            </Button>
            <Button variant="outline" onClick={handleExportDocx} loading={isExportingDocx}>
              <FileText className="w-4 h-4 mr-1" />
              Exportar Word
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Info Cards */}
        <div className="lg:col-span-1 space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader
              title={
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-neutral-400" />
                  <span>Estado del Contrato</span>
                </div>
              }
            />
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">Estado</span>
                <Badge variant={statusBadgeVariants[contract.status]}>
                  {statusLabels[contract.status]}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">Version</span>
                <span className="font-medium">{contract.version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">Creado</span>
                <span className="text-sm">{formatDate(contract.createdAt)}</span>
              </div>
              {contract.approvedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-600">Aprobado</span>
                  <span className="text-sm">{formatDate(contract.approvedAt)}</span>
                </div>
              )}
              {contract.sentAt && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-600">Enviado</span>
                  <span className="text-sm">{formatDate(contract.sentAt)}</span>
                </div>
              )}
              {contract.signedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-600">Firmado</span>
                  <span className="text-sm">{formatDate(contract.signedAt)}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Property Card */}
          <Card>
            <CardHeader
              title={
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-neutral-400" />
                  <span>Inmueble</span>
                </div>
              }
            />
            <div className="p-4 space-y-3 text-sm">
              <div>
                <span className="text-neutral-500">Direccion:</span>
                <div className="font-medium">{String(property.address || 'No especificado')}</div>
              </div>
              <div>
                <span className="text-neutral-500">Ciudad:</span>
                <div className="font-medium">{String(property.city || 'No especificado')}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-neutral-500">Tipo:</span>
                  <div className="font-medium">{String(property.propertyType || '-')}</div>
                </div>
                <div>
                  <span className="text-neutral-500">Estrato:</span>
                  <div className="font-medium">{String(property.stratum || '-')}</div>
                </div>
              </div>
              <div>
                <span className="text-neutral-500">Area:</span>
                <div className="font-medium">{String(property.area || '-')} m2</div>
              </div>
            </div>
          </Card>

          {/* Terms Card */}
          <Card>
            <CardHeader
              title={
                <div className="flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-neutral-400" />
                  <span>Terminos</span>
                </div>
              }
            />
            <div className="p-4 space-y-3 text-sm">
              <div>
                <span className="text-neutral-500">Canon Mensual:</span>
                <div className="font-medium text-lg">
                  {formatCurrency(Number(terms.monthlyRent) || 0)}
                </div>
              </div>
              <div>
                <span className="text-neutral-500">Administracion:</span>
                <div className="font-medium">
                  {formatCurrency(Number(terms.administrationFee) || 0)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-neutral-500">Inicio:</span>
                  <div className="font-medium">
                    {terms.startDate ? formatDate(String(terms.startDate)) : '-'}
                  </div>
                </div>
                <div>
                  <span className="text-neutral-500">Fin:</span>
                  <div className="font-medium">
                    {terms.endDate ? formatDate(String(terms.endDate)) : '-'}
                  </div>
                </div>
              </div>
              <div>
                <span className="text-neutral-500">Duracion:</span>
                <div className="font-medium">{String(terms.durationMonths || '-')} meses</div>
              </div>
            </div>
          </Card>

          {/* Parties Cards */}
          {tenant && (
            <Card>
              <CardHeader
                title={
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-neutral-400" />
                    <span>Arrendatario</span>
                  </div>
                }
              />
              <div className="p-4 space-y-2 text-sm">
                <div className="font-medium">
                  {tenant.personType === 'legal' && tenant.companyName
                    ? tenant.companyName
                    : `${tenant.firstName} ${tenant.lastName}`}
                </div>
                <div className="text-neutral-500">CC: {tenant.idNumber}</div>
                <div className="text-neutral-500">{tenant.phone}</div>
                <div className="text-neutral-500">{tenant.email}</div>
              </div>
            </Card>
          )}

          {owners.length > 0 && (
            <Card>
              <CardHeader
                title={
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-neutral-400" />
                    <span>Propietario{owners.length > 1 ? 's' : ''}</span>
                  </div>
                }
              />
              <div className="p-4 space-y-4">
                {owners.map((owner, i) => (
                  <div key={i} className="text-sm">
                    <div className="font-medium">
                      {owner.personType === 'legal' && owner.companyName
                        ? owner.companyName
                        : `${owner.firstName} ${owner.lastName}`}
                    </div>
                    <div className="text-neutral-500">CC: {owner.idNumber}</div>
                    <div className="text-neutral-500">{owner.phone}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {guarantors.length > 0 && (
            <Card>
              <CardHeader
                title={
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-neutral-400" />
                    <span>Deudores Solidarios</span>
                  </div>
                }
              />
              <div className="p-4 space-y-4">
                {guarantors.map((guarantor, i) => (
                  <div key={i} className="text-sm">
                    <div className="font-medium">
                      {guarantor.firstName} {guarantor.lastName}
                    </div>
                    <div className="text-neutral-500">CC: {guarantor.idNumber}</div>
                    <div className="text-neutral-500">{guarantor.phone}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Selected Clauses */}
          {contract.selectedClauses.length > 0 && (
            <Card>
              <CardHeader
                title={
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-neutral-400" />
                    <span>Clausulas Adicionales</span>
                  </div>
                }
              />
              <div className="p-4">
                <div className="text-sm text-neutral-600">
                  {contract.selectedClauses.length} clausula
                  {contract.selectedClauses.length !== 1 ? 's' : ''} seleccionada
                  {contract.selectedClauses.length !== 1 ? 's' : ''}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Right Column - Preview */}
        <div className="lg:col-span-2">
          <Card className="sticky top-4">
            <CardHeader
              title={
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-neutral-400" />
                  <span>Vista Previa del Contrato</span>
                </div>
              }
              action={
                contract.renderedHtml && (
                  <button
                    onClick={() => setFullscreenOpen(true)}
                    className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700"
                    title="Ver en pantalla completa"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                )
              }
            />
            <div className="p-4">
              {previewSrcDoc ? (
                <div className="border border-neutral-200 rounded-lg bg-white overflow-hidden">
                  <iframe
                    srcDoc={previewSrcDoc}
                    className="w-full border-0"
                    style={{ height: '80vh' }}
                    title="Vista previa del contrato"
                  />
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-500">
                  No hay vista previa disponible
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Approve Modal */}
      <Modal
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        title="Aprobar Contrato"
        description="El contrato quedara listo para enviar"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas aprobar este contrato? Una vez aprobado no podra ser editado.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setApproveModalOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleApprove} loading={isApproving}>
            Aprobar Contrato
          </Button>
        </ModalFooter>
      </Modal>

      {/* Send Email Modal */}
      {contract && (
        <SendContractModal
          open={sendEmailModalOpen}
          onClose={() => setSendEmailModalOpen(false)}
          contract={contract}
          onSuccess={handleEmailSent}
        />
      )}

      {/* Fullscreen Preview Modal */}
      <Modal open={fullscreenOpen} onClose={() => setFullscreenOpen(false)} size="fullscreen">
        <div className="flex items-center justify-between px-6 py-3 border-b border-neutral-200 -mx-6 -mt-6 mb-0">
          <h2 className="text-lg font-semibold text-neutral-900">Vista Previa del Contrato</h2>
          <button
            onClick={() => setFullscreenOpen(false)}
            className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {previewSrcDoc && (
          <iframe
            srcDoc={previewSrcDoc}
            className="w-full border-0 -mx-6 -mb-6 flex-1"
            style={{ marginTop: 0, minHeight: '80vh' }}
            title="Vista previa del contrato completa"
          />
        )}
      </Modal>
    </div>
  )
}
